"use client"

import React from "react"
import Autocomplete from "@mui/material/Autocomplete"
import { styled } from "@mui/material/styles"
import TextField from "@mui/material/TextField"
import { ControllerRenderProps } from "react-hook-form"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"
import { Label } from "@jobintrans/base-ui.components.atoms.typography"
import Spinner from "@jobintrans/base-ui.components.atoms.spinner"
import { Absolute, Box } from "@jobintrans/base-ui.components.atoms._atoms"
import Icon from "@jobintrans/base-ui.components.atoms.icon"
import { baseTheme } from "@jobintrans/base-ui.theme"
import { ErrorMessage, StyledBox, IconWrapper } from "./Input"

const StyledTextField = styled(TextField)(() => {
  return {
    "width": "100%",
    "background": "none",
    ".MuiInputBase-root": {
      "boxSizing": "border-box",
      "background": "none",
      "paddingLeft": baseTheme.font.XS,
      "paddingTop": baseTheme.font.XS,
      "paddingBottom": baseTheme.font.XS,
      "borderRadius": baseTheme.variable.borderRadius,

      "&:before, &:after": {
        display: "none",
      },

      "input, textarea": {
        boxSizing: "border-box",
        color: baseTheme.color.second,
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
        padding: "0",
        paddingRight: "10px",
      },
      "input": {
        "height": 25,

        "&::placeholder": {
          color: `${baseTheme.color.third} !important`,
          opacity: 1,
        },
      },
    },
    ".MuiInputAdornment-root": {
      margin: "0 !important",
      fontSize: baseTheme.font.L,
      lineHeight: baseTheme.font.XL,
      fontWeight: baseTheme.font.light,

      p: {
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
      },
    },
  }
})

export const InputAutocomplete = ({
  name,
  label,
  icon,
  inputValue,
  error,
  placeholder,
  loading,
  options,
  onChange,
  onInputChange = () => {},
  onOpen,
  noOptionsText = "No options",
  $background = "none",
  $borderColor,
  $width = "100%",
}: ControllerRenderProps &
  BaseStyles & {
    name?: string
    label?: string
    icon?: string
    placeholder?: string
    loading?: boolean
    inputValue?: string
    onOpen?: () => void
    onInputChange?: (e: any) => void
    error?: {
      type?: string
      message?: string
    }
    noOptionsText?: string
    options: {
      id: string
      label: string
    }[]
  }) => {
  return (
    <Autocomplete
      options={options}
      onChange={(_, data) => onChange(data)}
      noOptionsText={noOptionsText}
      inputValue={inputValue}
      onOpen={onOpen}
      onInputChange={(e, value, reason) => {
        if (e) {
          onInputChange(value)
        }
      }}
      defaultValue={null}
      filterOptions={x => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={params => (
        <Box data-name={name}>
          {label && (
            <Label $type="secondary" $marginBottom="XS">
              {label}
            </Label>
          )}
          <StyledBox
            $width={$width}
            $hasIcon={!!icon}
            $background={$background}
            $borderColor={$borderColor}
          >
            <StyledTextField placeholder={placeholder} variant="outlined" {...params} />
            {loading && (
              <IconWrapper>
                <Spinner $size="15px" $borderWidth={3} />
              </IconWrapper>
            )}
            {icon && (
              <Absolute
                $left={baseTheme.font.S}
                $top="50%"
                style={{ transform: "translateY(-50%)" }}
                $display="flex"
                $alignItems="center"
              >
                <Icon $size="15px" icon={icon} />
              </Absolute>
            )}
          </StyledBox>
          {error && <ErrorMessage>{error?.message ?? "To pole jest wymagane"}</ErrorMessage>}
        </Box>
      )}
    />
  )
}
